import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import {
  CheckCircle2,
  Circle,
  ExternalLink,
  FileCode,
  Map,
  Search,
  Bot,
  Code2,
  BarChart3,
  Globe,
  RefreshCw,
  ArrowRight,
} from "lucide-react";

export const Route = createFileRoute("/seo-checklist")({
  head: () => ({
    meta: [
      { title: "SEO Checklist — MEI Toolbox" },
      {
        name: "description",
        content:
          "Guia passo a passo para acompanhar sitemap, robots.txt, schema e indexação no Google Search Console do MEI Toolbox.",
      },
      { name: "robots", content: "noindex, follow" },
      { property: "og:title", content: "SEO Checklist — MEI Toolbox" },
      {
        property: "og:description",
        content: "Checklist interno: sitemap, robots, schema e status de indexação.",
      },
    ],
  }),
  component: SeoChecklistPage,
});

const SITE = "https://meitoolbox.com.br";
const STORAGE_KEY = "mei_seo_checklist_v1";

type Step = {
  id: string;
  title: string;
  description: string;
  action?: { label: string; href: string };
};

type Section = {
  id: string;
  title: string;
  icon: React.ReactNode;
  intro: string;
  steps: Step[];
};

const sections: Section[] = [
  {
    id: "sitemap",
    title: "1. Sitemap",
    icon: <Map className="h-5 w-5" />,
    intro:
      "O sitemap é um índice (sitemap.xml) que aponta para 3 sub-sitemaps: ferramentas, artigos e páginas estáticas. Ele é gerado automaticamente.",
    steps: [
      {
        id: "sitemap-index",
        title: "Abrir o sitemap index e conferir as 3 referências",
        description:
          "Deve listar sitemap-ferramentas.xml, sitemap-artigos.xml e sitemap-pages.xml.",
        action: { label: "Abrir sitemap.xml", href: `${SITE}/sitemap.xml` },
      },
      {
        id: "sitemap-tools",
        title: "Validar sitemap de ferramentas",
        description: "Confirmar que apenas ferramentas com available: true aparecem.",
        action: {
          label: "Abrir sitemap-ferramentas.xml",
          href: `${SITE}/sitemap-ferramentas.xml`,
        },
      },
      {
        id: "sitemap-articles",
        title: "Validar sitemap de artigos",
        description:
          "Rascunhos e arquivos com menos de 500 caracteres são excluídos automaticamente.",
        action: {
          label: "Abrir sitemap-artigos.xml",
          href: `${SITE}/sitemap-artigos.xml`,
        },
      },
      {
        id: "sitemap-pages",
        title: "Validar sitemap de páginas estáticas",
        description: "Home, /ferramentas, /artigos, /contato e /privacidade.",
        action: {
          label: "Abrir sitemap-pages.xml",
          href: `${SITE}/sitemap-pages.xml`,
        },
      },
      {
        id: "sitemap-submit",
        title: "Enviar sitemap.xml no Search Console",
        description: "Em Sitemaps, colar 'sitemap.xml' e enviar. Status deve ser 'Sucesso'.",
        action: {
          label: "Abrir Search Console",
          href: "https://search.google.com/search-console",
        },
      },
    ],
  },
  {
    id: "robots",
    title: "2. robots.txt",
    icon: <Bot className="h-5 w-5" />,
    intro: "O robots.txt informa aos buscadores o que pode ser rastreado e onde está o sitemap.",
    steps: [
      {
        id: "robots-open",
        title: "Abrir robots.txt em produção",
        description:
          "Deve permitir Allow: / e ter a linha Sitemap: https://meitoolbox.com.br/sitemap.xml.",
        action: { label: "Abrir robots.txt", href: `${SITE}/robots.txt` },
      },
      {
        id: "robots-test",
        title: "Testar com o robots.txt Tester",
        description:
          "Use o testador do Search Console para garantir que /ferramentas e /artigos estejam liberados.",
        action: {
          label: "Abrir robots Tester",
          href: "https://www.google.com/webmasters/tools/robots-testing-tool",
        },
      },
    ],
  },
  {
    id: "schema",
    title: "3. Schema / Dados Estruturados",
    icon: <Code2 className="h-5 w-5" />,
    intro: "JSON-LD em cada página ajuda o Google a entender o conteúdo e habilita rich results.",
    steps: [
      {
        id: "schema-home",
        title: "Validar a Home (WebSite + Organization)",
        description: "Cole a URL no Rich Results Test e confirme que não há erros.",
        action: {
          label: "Abrir Rich Results Test",
          href: `https://search.google.com/test/rich-results?url=${encodeURIComponent(SITE)}`,
        },
      },
      {
        id: "schema-tools",
        title: "Validar uma página de ferramenta",
        description: "Deve detectar SoftwareApplication / WebApplication e BreadcrumbList.",
        action: {
          label: "Testar /ferramentas/calculadora-das-mei",
          href: `https://search.google.com/test/rich-results?url=${encodeURIComponent(SITE + "/ferramentas/calculadora-das-mei")}`,
        },
      },
      {
        id: "schema-article",
        title: "Validar um artigo",
        description: "Deve detectar Article com headline, datePublished e author.",
        action: {
          label: "Testar /artigos/o-que-e-mei",
          href: `https://search.google.com/test/rich-results?url=${encodeURIComponent(SITE + "/artigos/o-que-e-mei")}`,
        },
      },
      {
        id: "schema-validator",
        title: "Validar com Schema.org Validator",
        description:
          "Segunda opinião — útil para schemas que o Google ainda não usa para rich results.",
        action: {
          label: "Abrir Schema Validator",
          href: "https://validator.schema.org/",
        },
      },
    ],
  },
  {
    id: "indexacao",
    title: "4. Status de Indexação",
    icon: <Search className="h-5 w-5" />,
    intro: "Acompanhe quais URLs o Google já indexou e quais foram descobertas mas não rastreadas.",
    steps: [
      {
        id: "index-coverage",
        title: "Conferir 'Páginas' no Search Console",
        description:
          "Veja indexadas vs não indexadas. Investigue motivos: 'Descoberta — não indexada', 'Rastreada — não indexada', 'Bloqueada por robots.txt'.",
        action: {
          label: "Abrir Páginas (Search Console)",
          href: "https://search.google.com/search-console/index",
        },
      },
      {
        id: "index-site",
        title: "Rodar site:meitoolbox.com.br",
        description: "Visão rápida do que já está no índice do Google.",
        action: {
          label: "Pesquisar no Google",
          href: "https://www.google.com/search?q=site%3Ameitoolbox.com.br",
        },
      },
      {
        id: "index-request",
        title: "Solicitar indexação das páginas-chave",
        description:
          "Inspecione cada URL prioritária (home, top 5 ferramentas, top 3 artigos) e clique em 'Solicitar indexação'.",
        action: {
          label: "Abrir Inspeção de URL",
          href: "https://search.google.com/search-console/inspect",
        },
      },
      {
        id: "index-performance",
        title: "Monitorar Desempenho semanalmente",
        description:
          "Acompanhe impressões, cliques, CTR e posição média por consulta e por página.",
        action: {
          label: "Abrir Desempenho",
          href: "https://search.google.com/search-console/performance/search-analytics",
        },
      },
    ],
  },
  {
    id: "metadados",
    title: "5. Metadados & Conteúdo",
    icon: <FileCode className="h-5 w-5" />,
    intro: "Cada rota define seu próprio head() com title, description e Open Graph.",
    steps: [
      {
        id: "meta-titles",
        title: "Conferir títulos < 60 caracteres",
        description:
          "Títulos longos são truncados. Mantenha foco em uma palavra-chave principal por página.",
      },
      {
        id: "meta-desc",
        title: "Conferir descriptions < 160 caracteres",
        description: "Inclua call-to-action curto e a palavra-chave principal.",
      },
      {
        id: "meta-h1",
        title: "Cada página deve ter exatamente um H1",
        description: "O H1 deve refletir o título e usar a palavra-chave principal.",
      },
      {
        id: "meta-og",
        title: "Validar Open Graph com prévia social",
        description:
          "Use o LinkedIn Post Inspector ou opengraph.dev para checar imagem, título e descrição.",
        action: {
          label: "Abrir OpenGraph.dev",
          href: "https://www.opengraph.dev/",
        },
      },
    ],
  },
  {
    id: "performance",
    title: "6. Core Web Vitals",
    icon: <BarChart3 className="h-5 w-5" />,
    intro: "Performance afeta ranking. Meta: LCP < 2.5s, INP < 200ms, CLS < 0.1.",
    steps: [
      {
        id: "cwv-psi",
        title: "Rodar PageSpeed Insights na home",
        description: "Conferir métricas mobile e desktop.",
        action: {
          label: "Abrir PageSpeed Insights",
          href: `https://pagespeed.web.dev/report?url=${encodeURIComponent(SITE)}`,
        },
      },
      {
        id: "cwv-tools",
        title: "Rodar PageSpeed em uma ferramenta pesada",
        description:
          "Calculadoras com muitos campos podem ter INP alto. Otimize handlers caso necessário.",
        action: {
          label: "Testar /ferramentas/calculadora-das-mei",
          href: `https://pagespeed.web.dev/report?url=${encodeURIComponent(SITE + "/ferramentas/calculadora-das-mei")}`,
        },
      },
      {
        id: "cwv-search",
        title: "Conferir 'Core Web Vitals' no Search Console",
        description: "Dados reais de campo (CrUX). Verde em mobile e desktop.",
        action: {
          label: "Abrir Core Web Vitals",
          href: "https://search.google.com/search-console/core-web-vitals",
        },
      },
    ],
  },
  {
    id: "manutencao",
    title: "7. Manutenção mensal",
    icon: <RefreshCw className="h-5 w-5" />,
    intro: "Rotina recorrente para manter o SEO saudável.",
    steps: [
      {
        id: "maint-broken",
        title: "Checar links quebrados",
        description: "Use o relatório de erros 404 do Search Console.",
      },
      {
        id: "maint-content",
        title: "Atualizar 1-2 artigos antigos",
        description: "Atualize datas, exemplos e estatísticas. Reenvie URL para indexação.",
      },
      {
        id: "maint-keywords",
        title: "Revisar consultas em alta",
        description:
          "Em Desempenho, ordene por impressões. Crie/expanda conteúdo para palavras-chave com CTR baixo.",
      },
      {
        id: "maint-backlinks",
        title: "Conferir backlinks",
        description: "Em Links → Sites com mais links. Avalie oportunidades de parceria.",
        action: {
          label: "Abrir Links",
          href: "https://search.google.com/search-console/links",
        },
      },
    ],
  },
];

const allStepIds = sections.flatMap((s) => s.steps.map((st) => st.id));

function SeoChecklistPage() {
  const [done, setDone] = useState<Record<string, boolean>>({});
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) setDone(JSON.parse(raw));
    } catch {
      // ignore
    }
    setHydrated(true);
  }, []);

  useEffect(() => {
    if (!hydrated) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(done));
    } catch {
      // ignore
    }
  }, [done, hydrated]);

  const toggle = (id: string) => setDone((d) => ({ ...d, [id]: !d[id] }));

  const reset = () => setDone({});

  const total = allStepIds.length;
  const completed = allStepIds.filter((id) => done[id]).length;
  const pct = total === 0 ? 0 : Math.round((completed / total) * 100);

  return (
    <>
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="absolute inset-0 bg-gradient-hero opacity-95" />
        <div className="relative container mx-auto px-4 py-16 md:py-20 text-white">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 backdrop-blur px-4 py-1.5 text-xs font-semibold uppercase tracking-wider">
            <Globe className="h-3.5 w-3.5" />
            Painel interno
          </div>
          <h1 className="mt-5 font-display text-4xl md:text-5xl font-extrabold leading-tight max-w-3xl text-balance">
            SEO Checklist
          </h1>
          <p className="mt-4 text-lg text-white/85 max-w-2xl">
            Guia passo a passo para acompanhar sitemap, robots.txt, schema e status de indexação do
            MEI Toolbox no Google Search Console.
          </p>

          {/* Progresso */}
          <div className="mt-8 max-w-xl rounded-2xl border border-white/20 bg-white/10 backdrop-blur p-5">
            <div className="flex items-center justify-between text-sm font-medium">
              <span>Progresso</span>
              <span>
                {completed}/{total} ({pct}%)
              </span>
            </div>
            <div className="mt-3 h-2 w-full rounded-full bg-white/20 overflow-hidden">
              <div className="h-full bg-white transition-all" style={{ width: `${pct}%` }} />
            </div>
            <button
              onClick={reset}
              className="mt-4 inline-flex items-center gap-2 text-xs font-semibold text-white/80 hover:text-white transition"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Resetar checklist
            </button>
          </div>
        </div>
      </section>

      {/* ÍNDICE */}
      <section className="container mx-auto px-4 py-10">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {sections.map((s) => {
            const c = s.steps.filter((st) => done[st.id]).length;
            return (
              <a
                key={s.id}
                href={`#${s.id}`}
                className="rounded-2xl border border-border bg-card p-4 shadow-soft hover:border-primary/40 transition"
              >
                <div className="flex items-center gap-2 text-primary">
                  {s.icon}
                  <span className="text-xs font-semibold uppercase tracking-wider">
                    {c}/{s.steps.length}
                  </span>
                </div>
                <div className="mt-2 font-semibold text-foreground text-sm leading-snug">
                  {s.title}
                </div>
              </a>
            );
          })}
        </div>
      </section>

      {/* SEÇÕES */}
      <section className="container mx-auto px-4 pb-20 max-w-3xl space-y-12">
        {sections.map((s) => (
          <div key={s.id} id={s.id} className="scroll-mt-24">
            <div className="flex items-center gap-3 mb-2">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
                {s.icon}
              </div>
              <h2 className="font-display text-2xl font-bold text-foreground">{s.title}</h2>
            </div>
            <p className="text-muted-foreground mb-5">{s.intro}</p>

            <ul className="space-y-3">
              {s.steps.map((st) => {
                const checked = !!done[st.id];
                return (
                  <li
                    key={st.id}
                    className={`rounded-2xl border p-4 transition ${
                      checked ? "border-primary/30 bg-primary/5" : "border-border bg-card"
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <button
                        onClick={() => toggle(st.id)}
                        className="mt-0.5 shrink-0 text-primary hover:scale-110 transition"
                        aria-label={checked ? "Marcar como pendente" : "Marcar como feito"}
                      >
                        {checked ? (
                          <CheckCircle2 className="h-5 w-5" />
                        ) : (
                          <Circle className="h-5 w-5 text-muted-foreground" />
                        )}
                      </button>
                      <div className="flex-1 min-w-0">
                        <div
                          className={`font-semibold text-foreground ${
                            checked ? "line-through opacity-60" : ""
                          }`}
                        >
                          {st.title}
                        </div>
                        <p className="mt-1 text-sm text-muted-foreground leading-snug">
                          {st.description}
                        </p>
                        {st.action && (
                          <a
                            href={st.action.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:underline"
                          >
                            {st.action.label}
                            <ExternalLink className="h-3.5 w-3.5" />
                          </a>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}

        <div className="mt-12 rounded-2xl border border-border bg-surface/60 p-6 text-center">
          <p className="text-sm text-muted-foreground">Voltar para as ferramentas?</p>
          <Link
            to="/ferramentas"
            className="mt-4 inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-bold text-primary-foreground shadow-soft hover:opacity-90 transition"
          >
            Ver todas as ferramentas <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </>
  );
}
